import { addBreadcrumb } from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { bankingAccountsKeys } from "Api/Queries/Accounts/useBankingAccounts";
import { bankAccountsByBankKeys } from "Api/Queries/Accounts/useBankAccountsByBank";
import { deleteBankingAccountsAccountID } from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { chunk } from "lodash-es";

// A branch account can drop ~40 company accounts in one go; fired all at once the
// deletes took 8-17 s each and some failed, so they go out a few at a time.
const DELETE_CONCURRENCY = 4;

const removeBankAccount = processResponseWithApiProblemDetails(
  (accountId: number) => deleteBankingAccountsAccountID(accountId),
);

export const useRemoveBankAccounts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (accountIds: Array<number>) => {
      const results: Array<PromiseSettledResult<unknown>> = [];
      for (const batch of chunk(accountIds, DELETE_CONCURRENCY)) {
        results.push(
          ...(await Promise.allSettled(batch.map(removeBankAccount))),
        );
      }

      const failures = results.filter(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected",
      );
      if (failures.length === 0) {
        return;
      }

      // One Sentry report per batch (MutationCache.onError), with the full tally in the trail.
      addBreadcrumb({
        category: "api.error",
        level: "warning",
        message: `Removing bank accounts: ${failures.length} of ${accountIds.length} failed`,
        data: {
          failedAccountIds: accountIds.filter(
            (_, index) => results[index].status === "rejected",
          ),
        },
      });
      throw failures[0].reason;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: bankingAccountsKeys.all });
      queryClient.invalidateQueries({ queryKey: bankAccountsByBankKeys.all });
    },
  });
};
